import React from "react";

interface MatchCardProps {
  p1: string;
  p2: string;
  p1LoginId?: string;
  p2LoginId?: string;
  s1: number | null;
  s2: number | null;
  isLive?: boolean;
  matchNum: string | number;
  isAdmin: boolean;
  isFinalRound?: boolean;
  round?: number;
  blockNumber?: number;
  onResultClick?: (
    round: number,
    blockNumber: number,
    p1Name: string,
    p2Name: string,
    p1Wins: number | null,
    p2Wins: number | null,
  ) => void;
}

const MatchCard: React.FC<MatchCardProps> = ({
  p1,
  p2,
  p1LoginId,
  p2LoginId,
  s1,
  s2,
  isLive,
  matchNum,
  isAdmin,
  isFinalRound = false,
  round = 0,
  blockNumber = 0,
  onResultClick,
}) => {
  const p1Winner = s1 !== null && s2 !== null && s1 > s2;
  const p2Winner = s1 !== null && s2 !== null && s2 > s1;
  const isPending = s1 === null && s2 === null;

  const renderPlayerField = (
    playerName: string,
    loginId: string | undefined,
    isWinner: boolean,
  ) => {
    return (
      <span className="flex flex-col min-w-0 pr-2">
        <span
          className={`text-sm truncate ${
            isWinner
              ? "font-black text-amber-700"
              : isPending
                ? "text-slate-400"
                : "font-medium text-slate-600"
          }`}
        >
          {playerName}
        </span>
        {loginId && (
          <span className="text-[10px] text-slate-400 truncate leading-tight">
            {loginId}
          </span>
        )}
      </span>
    );
  };

  const renderScoreField = (score: number | null, isWinner: boolean) => {
    return (
      <span
        className={`text-lg font-black ${
          isWinner
            ? "text-amber-600"
            : isPending
              ? "text-slate-300"
              : "text-slate-400"
        }`}
      >
        {score === null ? "-" : score === 999 ? "欠場" : score}
      </span>
    );
  };

  return (
    <div
      onClick={() => {
        if (isAdmin && onResultClick) {
          onResultClick(round, blockNumber, p1, p2, s1, s2);
        }
      }}
      className={`w-56 h-[120px] rounded-lg flex flex-col relative overflow-hidden group shrink-0 transition-all duration-200 ${
        isAdmin ? "cursor-pointer" : ""
      } ${
        isFinalRound
          ? "bg-white border-2 border-amber-300 shadow-[0_4px_15px_rgb(0,0,0,0.02)] hover:border-amber-400"
          : "bg-white border border-slate-200 shadow-[0_4px_15px_rgb(0,0,0,0.02)] hover:border-slate-300"
      }`}
    >
      {isLive && (
        <div className="absolute top-0 left-0 w-full h-1 bg-rose-500 animate-pulse" />
      )}
      <div
        className={`absolute top-1 left-2 text-[8px] font-bold uppercase tracking-widest ${
          isFinalRound ? "text-amber-400" : "text-slate-300"
        }`}
      >
        {isFinalRound ? "FINAL" : `Match ${matchNum}`}
      </div>
      <div
        className={`flex justify-between items-center px-3 pt-4 pb-2 border-b border-slate-100 relative ${
          p1Winner ? "bg-amber-50/70" : ""
        }`}
      >
        {p1Winner && (
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400" />
        )}
        {renderPlayerField(p1, p1LoginId, p1Winner)}
        {renderScoreField(s1, p1Winner)}
      </div>
      <div
        className={`flex justify-between items-center px-3 py-2 relative ${
          p2Winner ? "bg-amber-50/70" : ""
        }`}
      >
        {p2Winner && (
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400" />
        )}
        {renderPlayerField(p2, p2LoginId, p2Winner)}
        {renderScoreField(s2, p2Winner)}
      </div>
    </div>
  );
};

export default MatchCard;
